import { Divider, Grid } from '@mui/material'
import React from 'react'
import InfoBlock from '../components/InfoBlock'
import { SimpleCardBlock } from '../components/SimpleCardBlock'
import WarrantOrigin from './WarrantOrigin'
import WarrantOtherInfo, { warrantOtherInfoHasContent } from './WarrantOtherInfo'
import WarrantOutcome, { warrantOutcomeHasContent } from './WarrantOutcome'
import WarrantTradingStats from './WarrantTradingStats'
import WarrantTerms from './WarrantTerms'
import { ExtendedWarrantSourceType, SourcesTable } from '../../components/entities/SourcesTable'
import UnderwritersAndGuarantorsTable from '../components/UnderwritersAndGuarantorsTable'
import { WarrantEntity } from '../../types/WarrantEntity'
import { SourceEntity } from 'types/SourceEntity'
import { WarrantSummary } from './WarrantSummary'

type ExtendedSourceEntity = Omit<SourceEntity, 'sourceType'> & {
    sourceType: ExtendedWarrantSourceType | 'PublicationWarrant'
}

function WarrantDetailsView({ warrant }: { warrant: WarrantEntity }) {
    const publications =
        warrant.origins?.reduce((acc, curr) => {
            if (!curr.sources) {
                return acc
            }

            const publicationSources = curr.sources.filter((source) => source.sourceType === 'Publication')

            for (const publicationSource of publicationSources) {
                const alreadyExists = acc.find((source) => source.sourceUrl === publicationSource.sourceUrl)

                if (!alreadyExists) {
                    acc.push({ ...publicationSource, sourceType: 'PublicationWarrant' })
                }
            }

            return acc
        }, [] as Array<ExtendedSourceEntity>) || []

    const sources: ExtendedSourceEntity[] = [...warrant.sources, ...publications]

    return (
        <Grid container spacing={6}>
            {!!warrant.cancelled && (
                <Grid size={12}>
                    <SimpleCardBlock>
                        Notera att denna teckningsoption är avbruten. Innehållet som återges nedan återspeglar den
                        information som fanns tillgänglig för marknaden.
                    </SimpleCardBlock>
                </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
                <WarrantSummary isDetailsPage isLoading={false} warrant={warrant} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                {/* Optionens ursprung? */}
                <InfoBlock title='Optionens ursprung'>
                    <WarrantOrigin warrant={warrant} />
                </InfoBlock>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <InfoBlock title='Villkor'>
                    <WarrantTerms warrant={warrant} />
                </InfoBlock>
            </Grid>
            {(warrant.investors.length && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <InfoBlock title='Tecknings- och garantiåtagare'>
                        <UnderwritersAndGuarantorsTable
                            currency={warrant.currency}
                            fees={warrant.fees || []}
                            hideTotalColumns
                            investors={warrant.investors}
                            isUnit={false}
                            totalOfferingSize={0}
                        />
                    </InfoBlock>
                </Grid>
            )) ||
                null}
            {warrantOutcomeHasContent(warrant) && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <InfoBlock title='Utfall'>
                        <WarrantOutcome warrant={warrant} />
                    </InfoBlock>
                </Grid>
            )}
            {!warrant.unlisted && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <InfoBlock title='Handelsstatistik'>
                        <WarrantTradingStats warrant={warrant} />
                    </InfoBlock>
                </Grid>
            )}
            {warrantOtherInfoHasContent(warrant, sources) && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <InfoBlock title='Övrigt'>
                        {warrant.externalComment && (
                            <>
                                <WarrantOtherInfo warrant={warrant} />
                                <Divider sx={{ mt: 3 }} />
                            </>
                        )}
                        {sources.length > 0 && <SourcesTable sources={sources} />}
                    </InfoBlock>
                </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
                {/* Teckningsoption sammanhang */}
                {/* <WarrantContext warrant={warrant} /> */}
            </Grid>
        </Grid>
    )
}

export default WarrantDetailsView
